import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { StaticGoogleMap, Marker as StaticMarker, Path as StaticPath } from "react-static-google-map";

import {
  checkExperienceCookieConsent,
  generateRandomId,
  getGoogleKey,
  getZoomLevel,
  getZoomLevelFromBounds,
  openCookiePreferences,
  useCookieConsent,
} from "utils";

import { Coordinates, FieldDetails, FieldShape } from "models";

import { Icon, LazyLoadImage, Link, Tooltip, Trans } from "components";

// Generic map placeholder (design asset), heavily blurred, used as the "map locked" (missing Experience consent)
// backdrop — never the user's real field and no Google request, so it works before consent is given.
const BLURRED_MAP_IMAGE = "/images/misc/map-placeholder.png";

type MarkerProps = {
  label?: string;
  iconUrl?: string;
};

/** A full field shape, or a bare polygon (e.g. a matched registry parcel) with no field metadata. */
type MapGeometry = Pick<FieldShape, "geometry"> & Partial<Omit<FieldShape, "geometry">>;

type MapStaticProps = {
  defaultZoom?: number | null;
  field?: FieldDetails | null;
  geometry?: MapGeometry | null;
  /** Second polygon drawn in a distinct color, e.g. a matched registry parcel to compare against `geometry`. */
  secondaryGeometry?: Pick<FieldShape, "geometry"> | null;
  /** More polygons drawn like `geometry`, e.g. the other parcels of a grouped document field. */
  extraGeometries?: Coordinates[][] | null;
  /** Override `geometry`'s stroke/fill (Google static-maps hex format, e.g. "0x1A73E8FF"). Defaults to white. */
  primaryColor?: string;
  primaryFillColor?: string;
  marker?: MarkerProps | boolean;
  location?: Coordinates | null;
  iconUrl?: string | null;
  /** Show +/- buttons that let the viewer zoom the static map in/out. */
  zoomable?: boolean;
  /** Let the viewer drag to re-center the static map (re-fetches the image around the new center on drag release). */
  pannable?: boolean;
  /** Extra zoom levels added on top of the auto fit-to-bounds zoom (each level ~2x). */
  zoomBoost?: number;
};

/** Degrees of longitude per pixel at zoom 0 (Web Mercator); halves with every zoom level. Latitude scales by cos(lat). */
const DEG_LNG_PER_PIXEL_AT_ZOOM_0 = 156543.03392 / 111320;

// Hoisted to module scope so `as` gets a stable component identity across renders — an inline arrow here would make
// React remount the underlying image (dropping the previously-loaded frame) on every zoom/pan re-render.
const StaticMapLazyImg = (props: React.ComponentPropsWithRef<typeof LazyLoadImage>) => <LazyLoadImage {...props} delay={2000} />;

const DEFAULT_ICONS = [
  "https://shared.klim.eco/maps/green_2_s.png",
  "https://shared.klim.eco/maps/green_2_m.png",
  "https://shared.klim.eco/maps/green_2_l.png",
  "https://shared.klim.eco/maps/red_s.png",
  "https://shared.klim.eco/maps/red_m.png",
  "https://shared.klim.eco/maps/red_l.png",
];

export const MapStatic = memo(
  ({
    defaultZoom = null,
    field,
    geometry,
    secondaryGeometry,
    extraGeometries,
    primaryColor = "0xFFFFFFFF",
    primaryFillColor = "0xFFFFFF33",
    marker,
    location,
    iconUrl = null,
    zoomable = false,
    pannable = false,
    zoomBoost = 0,
  }: MapStaticProps) => {
    const [zoom, setZoom] = useState(15);
    const [height, setHeight] = useState(0);
    const [width, setWidth] = useState(0);
    const [mapId, setMapId] = useState<string | null>(null);
    const [centerOverride, setCenterOverride] = useState<Coordinates | null>(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const dragStartRef = useRef<{ x: number; y: number; center: Coordinates } | null>(null);
    const hasCookieConsent = useCookieConsent(checkExperienceCookieConsent);

    const { lat, lng } = location ?? { lat: 0, lng: 0 };

    const updateZoom = useCallback(() => {
      if (defaultZoom) {
        setZoom(defaultZoom);
      } else if (geometry?.geometry) {
        const bounds = [...geometry.geometry, ...(secondaryGeometry?.geometry ?? []), ...(extraGeometries ?? []).flat()];
        setZoom(Math.min(21, getZoomLevelFromBounds(bounds) + zoomBoost));
      } else if (field) {
        setZoom(getZoomLevel(field, width * height <= 100000));
      }
    }, [defaultZoom, field, geometry, secondaryGeometry, extraGeometries, width, height, zoomBoost]);

    useEffect(() => {
      if (!mapId) return;

      const el = document.getElementById(mapId);
      if (!el) return;

      const containerWidth = el.getBoundingClientRect().width;
      const containerHeight = el.getBoundingClientRect().height;

      if (containerWidth > 0 && containerHeight > 0) {
        const aspectRatio = containerHeight / containerWidth;
        const useMinVals = containerWidth <= 125 || containerHeight <= 125;
        setWidth(useMinVals ? 125 : containerWidth);
        setHeight(useMinVals ? 125 * aspectRatio : containerHeight);
      } else {
        setTimeout(() => {
          const id = generateRandomId();
          setMapId(id);
        }, 1000);
      }
    }, [mapId, width, height, zoom]);

    useEffect(() => {
      if (mapId) return;
      const id = generateRandomId();
      setMapId(id);
    }, [lat, lng, zoom, field?.id, mapId]);

    useEffect(() => {
      updateZoom();
    }, [lat, lng, updateZoom]);

    const markerLocation = geometry?.center
      ? `${geometry.center.lat},${geometry.center.lng}`
      : field
      ? `${field.location.lat},${field.location.lng}`
      : `${lat},${lng}`;

    const markerIconUrl = iconUrl ?? DEFAULT_ICONS[1];
    const markerLabel = typeof marker === "object" && marker !== null && marker.label ? marker.label : undefined;

    const mapCenter: Coordinates = geometry?.center ?? (field ? field.location : { lat, lng });
    const effectiveCenter = centerOverride ?? mapCenter;

    const handlePanStart = useCallback(
      (e: React.MouseEvent) => {
        if (!pannable) return;
        e.preventDefault();
        dragStartRef.current = { x: e.clientX, y: e.clientY, center: effectiveCenter };

        const handleMouseMove = (moveEvent: MouseEvent) => {
          if (!dragStartRef.current) return;
          setDragOffset({ x: moveEvent.clientX - dragStartRef.current.x, y: moveEvent.clientY - dragStartRef.current.y });
        };
        const handleMouseUp = (upEvent: MouseEvent) => {
          if (dragStartRef.current) {
            const dx = upEvent.clientX - dragStartRef.current.x;
            const dy = upEvent.clientY - dragStartRef.current.y;
            const degLngPerPixel = DEG_LNG_PER_PIXEL_AT_ZOOM_0 / 2 ** zoom;
            const degLatPerPixel = degLngPerPixel * Math.cos((dragStartRef.current.center.lat * Math.PI) / 180);
            setCenterOverride({
              lat: dragStartRef.current.center.lat + dy * degLatPerPixel,
              lng: dragStartRef.current.center.lng - dx * degLngPerPixel,
            });
          }
          dragStartRef.current = null;
          setDragOffset({ x: 0, y: 0 });
          window.removeEventListener("mousemove", handleMouseMove);
          window.removeEventListener("mouseup", handleMouseUp);
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
      },
      [pannable, effectiveCenter, zoom]
    );

    const mapConsentTooltipLink = (
      <Link
        type="tertiary-link"
        className="tertiary-link static-map-consent-link"
        onClick={(e) => {
          e.stopPropagation();
          openCookiePreferences();
        }}
      />
    );
    const mapConsentTooltipIcon = <Icon iconName="tabler/settings" size="xs" color="#fff" />;
    const blurredMapPreview = (
      <div className="static-map-blurred-preview">
        <LazyLoadImage alt="" src={BLURRED_MAP_IMAGE} width="100%" height="100%" />
      </div>
    );

    return (
      <div className="static-map-card" id={mapId ?? undefined}>
        {!hasCookieConsent && (
          <Tooltip
            placement="right"
            className="static-map-consent-tooltip"
            title={<Trans i18nKey="global:mapCookieConsent.tooltip" components={[mapConsentTooltipLink, mapConsentTooltipIcon]} />}
          >
            {blurredMapPreview}
          </Tooltip>
        )}
        {hasCookieConsent && height > 0 && width > 0 && (
          <div
            className="static-map-pan-surface"
            style={pannable ? { transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`, cursor: "grab" } : undefined}
            onMouseDown={handlePanStart}
          >
            <StaticGoogleMap
              key={mapId}
              as={StaticMapLazyImg}
              scale={2}
              size={`${Math.floor(width)}x${Math.floor(height)}`}
              apiKey={getGoogleKey()}
              center={`${effectiveCenter.lat},${effectiveCenter.lng}`}
              zoom={zoom}
              maptype="satellite"
            >
              {geometry?.geometry && <StaticPath color={primaryColor} fillcolor={primaryFillColor} points={geometry.geometry} weight={2} />}
              {extraGeometries?.map((points, index) => <StaticPath key={index} color={primaryColor} fillcolor={primaryFillColor} points={points} weight={2} />)}
              {secondaryGeometry?.geometry && <StaticPath color={"0xFF8C00FF"} fillcolor={"0xFF8C0033"} points={secondaryGeometry.geometry} weight={2} />}
              {marker && <StaticMarker iconURL={markerIconUrl} location={markerLocation} label={markerLabel} />}
            </StaticGoogleMap>
          </div>
        )}
        {zoomable && hasCookieConsent && height > 0 && width > 0 && (
          <div className="static-map-zoom-controls">
            <button type="button" aria-label="Zoom in" onClick={() => setZoom((z) => Math.min(z + 1, 21))}>
              <Icon iconName="tabler/zoom-in" size="xs" />
            </button>
            <button type="button" aria-label="Zoom out" onClick={() => setZoom((z) => Math.max(z - 1, 1))}>
              <Icon iconName="tabler/zoom-out" size="xs" />
            </button>
          </div>
        )}
      </div>
    );
  }
);

export default MapStatic;
